


































































































































































































































import {
	defineComponent,
	PropType,
	Ref,
	ref,
	watch,
} from '@vue/composition-api';
import deepequal from 'deep-equal';
import FormSection from '@front-app/components/form/FormSection.vue';
import FormLabel from '@front-app/components/form/FormLabel.vue';
import FormError from '@front-app/components/form/FormError.vue';
import FormInput from '@front-app/components/form/FormInput.vue';
import RadioButton from '@front-app/components/inputs/RadioButton.vue';
import { FullUser, FullUserOccupationRequest } from '@front-app/services/UserApi/UserModel';
import TrAddress from '@front-app/components/TrAddress.vue';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import EditorWrapper from '@front-app/components/layouts/form/EditorWrapper.vue';
import { v } from '@front-app/components/form/validation/useValidation';
import { required } from '@front-app/components/form/validation/validators';
import { Spacing } from '@front-app/components/constants';
import CountryPicker from '@front-app/components/inputs/CountryPicker.vue';
import RegionPicker from '@front-app/components/inputs/RegionPicker.vue';
import { CurrentUserStore } from '@front-app/store/singletons/User';
import { CountryCode, RegionCode } from '@front-app/services/SharedModels';
import { DEFAULT_COUNTRY_CODE, EMPTY_REGION } from '@front-app/components/inputs/CountryPickerData';
import { usePostalMask } from '@front-app/components/inputs/util';

enum MyLocationType {
	COMPANY,
	MY_LOCATION,
}

export default defineComponent({
	components: {
		FormSection,
		FormLabel,
		FormError,
		FormInput,
		RadioButton,
		TrAddress,
		EditorWrapper,
		CountryPicker,
		RegionPicker,
	},
	props: {
		user: {
			type: Object as PropType<FullUser>,
			required: true,
		},
	},
	setup(props) {
		const locationType = ref(MyLocationType.COMPANY);
		watch(() => [
			props.user.currentOccupation.location,
			props.user.currentOccupation.employer.address,
		], ([userLocation, baseOrgLocation]) => {
			locationType.value = deepequal(userLocation, baseOrgLocation) ? MyLocationType.COMPANY : MyLocationType.MY_LOCATION;
		}, { immediate: true });

		// Field values
		const country: Ref<CountryCode | null> = ref(DEFAULT_COUNTRY_CODE);
		const region: Ref<RegionCode | null> = ref(EMPTY_REGION);
		const city = ref('');
		const street1 = ref('');
		const street2 = ref('');
		const postalCode = ref('');
		const phone: Ref<null | string> = ref(null);
		const extension: Ref<null | string> = ref(null);
		const mobile: Ref<null | string> = ref(null);
		const fax: Ref<null | string> = ref(null);
		// End Fields values

		watch(() => props.user.currentOccupation.location, (loc) => {
			const makeEmpty = deepequal(props.user.currentOccupation.location, props.user.currentOccupation.employer.address);
			country.value = makeEmpty ? DEFAULT_COUNTRY_CODE : loc.country;
			region.value = makeEmpty ? EMPTY_REGION : loc.provStateRegion;
			city.value = makeEmpty ? '' : loc.city;
			street1.value = makeEmpty ? '' : loc.street1;
			street2.value = makeEmpty ? '' : loc.street2;
			postalCode.value = makeEmpty ? '' : loc.postalCode;
			phone.value = makeEmpty ? null : loc.contactInfo.phone;
			extension.value = makeEmpty ? null : loc.contactInfo.extension;
			mobile.value = makeEmpty ? null : loc.contactInfo.mobile;
			fax.value = makeEmpty ? null : loc.contactInfo.fax;
		}, { immediate: true });

		// Validation
		const postalMask = usePostalMask(country);
		const validation = v({
			country: {
				value: country,
				rules: [required()],
			},
			region: {
				value: region,
				rules: [required()],
			},
			city: {
				value: city,
				rules: [required()],
			},
			street1: {
				value: street1,
				rules: [required()],
			},
			postalCode: {
				value: postalCode,
				rules: [required()],
			},
			phone: {
				value: phone,
				rules: [required()],
			},
		});

		const {
			track,
			isLoading,
			isError,
			isSuccess,
		} = useAsyncStatus();

		const getUserUpdateRequestData = (): Partial<FullUserOccupationRequest> => {
			if (locationType.value === MyLocationType.COMPANY) {
				const { address } = props.user.currentOccupation.employer;
				return {
					location: {
						street1: address.street1,
						street2: address.street2,
						city: address.city,
						province: address.provStateRegion || undefined,
						country: address.country || undefined,
						postal: address.postalCode,
						contactInfo: {
							phone: address.contactInfo.phone,
							extension: address.contactInfo.extension,
							mobile: address.contactInfo.mobile,
							fax: address.contactInfo.fax,
						},
					},
				};
			}
			return {
				location: {
					street1: street1.value,
					street2: street2.value,
					city: city.value,
					province: region.value || undefined,
					country: country.value || undefined,
					postal: postalCode.value,
					contactInfo: {
						phone: phone.value || '',
						extension: extension.value || '',
						mobile: mobile.value || '',
						fax: fax.value || '',
					},
				},
			};
		};

		const saveUser = async () => {
			const currentOccupation = getUserUpdateRequestData();
			await track(CurrentUserStore.actions.update({ currentOccupation }));
		};
		return {
			MyLocationType,
			locationType,
			// Values
			country,
			region,
			city,
			street1,
			street2,
			postalCode,
			phone,
			extension,
			mobile,
			fax,
			// Validation
			postalMask,
			validation,
			// Saving...
			saveUser,
			isLoading,
			isError,
			isSuccess,
			// Util
			Spacing,
		};
	},
});
